import { useTu } from "@src/appV2/i18n/useTu";
import { CircularProgress, Tooltip } from "@mui/material";
import { useModalState } from "@src/lib/ui-react";
import { Button, type ButtonSize } from "../../../components/Button";
import { useGetWorkerLockStatus } from "../api/useGetWorkerLockStatus";
import { TOOLTIP_TEXTS } from "../constants";
import { LockedByOtherDialog } from "./LockedByOtherDialog";
import { LockFlowDialog } from "./LockFlowDialog";
import { UnlockConfirmationDialog } from "./UnlockConfirmationDialog";

interface InHomeLockToggleProps {
  workerId: string;
  workplaceId: string;
  size?: ButtonSize;
}

export function InHomeLockToggle(props: InHomeLockToggleProps) {
  const { tu } = useTu("chat");

  const { workerId, workplaceId, size = "small" } = props;

  const { isLoading, isLocked, lockedByCurrentWorkplace, lockData } = useGetWorkerLockStatus({
    workerId,
    workplaceId,
  });

  const lockFlowModalState = useModalState();
  const unlockModalState = useModalState();
  const lockedByOtherModalState = useModalState();

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  if (!isLocked) {
    return (
      <>
        <Tooltip title={TOOLTIP_TEXTS.lock}>
          <Button
            size={size}
            variant="warning"
            startIconType="padlock-locked"
            onClick={lockFlowModalState.openModal}
          >
            {tu("Lock")}
          </Button>
        </Tooltip>
        <LockFlowDialog
          modalState={lockFlowModalState}
          workerId={workerId}
          workplaceId={workplaceId}
        />
      </>
    );
  }

  if (lockedByCurrentWorkplace) {
    return (
      <>
        <Tooltip title={TOOLTIP_TEXTS.unlock}>
          <Button
            size={size}
            variant="outlined"
            startIconType="padlock-locked"
            onClick={unlockModalState.openModal}
          >
            {tu("Unlock")}
          </Button>
        </Tooltip>
        <UnlockConfirmationDialog
          modalState={unlockModalState}
          lockReason={lockData?.attributes.lockReason ?? ""}
          unlockCondition={lockData?.attributes.unlockCondition ?? ""}
          lockId={lockData?.id ?? ""}
          workplaceId={workplaceId}
        />
      </>
    );
  }

  return (
    <>
      <Tooltip title={TOOLTIP_TEXTS.lockedByOther}>
        <Button
          size={size}
          variant="danger"
          startIconType="padlock-locked"
          onClick={lockedByOtherModalState.openModal}
        >
          {tu("Locked")}
        </Button>
      </Tooltip>
      <LockedByOtherDialog modalState={lockedByOtherModalState} />
    </>
  );
}
